// AWS API Gateway 설정
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://your-api-gateway-url.amazonaws.com/dev';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = null;
  }

  // 인증 토큰 설정
  setToken(token) {
    this.token = token;
  }

  // 기본 헤더 생성
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  // GET 요청
  async get(endpoint, params = {}) {
    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // 쿼리 파라미터 추가
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.getHeaders(),
    });

    return this.handleResponse(response);
  }

  // POST 요청
  async post(endpoint, data = {}) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  // PUT 요청
  async put(endpoint, data = {}) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  // DELETE 요청
  async delete(endpoint) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    return this.handleResponse(response);
  }

  // 에러 메시지 추출 함수 (구조화된 에러 객체를 사용자 친화적 메시지로 변환)
  extractErrorMessage(data) {
    // 문자열 에러 메시지인 경우
    if (typeof data.error === 'string') {
      return data.error;
    }
    
    // 구조화된 에러 객체인 경우
    if (data.error && typeof data.error === 'object') {
      // AWS 에러의 경우 details.awsErrorMessage 우선 사용
      if (data.error.details?.awsErrorMessage) {
        const awsMessage = data.error.details.awsErrorMessage;
        
        // AWS 에러 메시지를 한국어로 변환
        if (awsMessage.includes('disableApiTermination')) {
          return '이 인스턴스는 종료 방지가 설정되어 있습니다. 인스턴스 설정에서 종료 방지를 해제한 후 다시 시도해주세요.';
        }
        
        if (awsMessage.includes('OperationNotPermitted')) {
          return '작업이 허용되지 않습니다. 인스턴스 상태 또는 권한을 확인해주세요.';
        }
        
        return awsMessage;
      }
      
      // 일반적인 에러 메시지
      if (data.error.message) {
        return data.error.message;
      }
    }
    
    // 기본 메시지 반환
    return data.message || null;
  }

  // 응답 처리
  async handleResponse(response) {
    let data;
    
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }

    if (!response.ok) {
      // 권한 관련 오류 처리
      if (response.status === 403) {
        const errorMessage = data.message || '권한이 없습니다. 관리자에게 문의하세요.';
        
        // 권한 오류 시 사용자에게 알림 표시
        if (typeof window !== 'undefined' && window.alert) {
          setTimeout(() => {
            alert(`접근 권한 없음: ${errorMessage}`);
          }, 100);
        }
        
        const error = new Error(errorMessage);
        error.status = 403;
        error.data = data;
        error.type = 'PERMISSION_DENIED';
        throw error;
      }
      
      // 인증 관련 오류 처리 (makeRequest에서 이미 처리했지만 추가 안전장치)
      if (response.status === 401) {
        const errorMessage = data.message || '인증이 만료되었습니다. 다시 로그인해주세요.';
        console.warn('401 오류가 handleResponse에 도달함 (토큰 갱신 실패?)');
        
        const error = new Error(errorMessage);
        error.status = 401;
        error.data = data;
        error.type = 'AUTHENTICATION_FAILED';
        throw error;
      }
      
      // 기타 오류 처리
      const errorMessage = this.extractErrorMessage(data) || `HTTP error! status: ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  // 연결 테스트
  async healthCheck() {
    try {
      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      return response.ok;
    } catch (error) {
      console.error('API 연결 테스트 실패:', error);
      return false;
    }
  }
}

// 개발 환경에서 사용할 수 있는 Mock API 서비스
class MockApiService extends ApiService {
  constructor() {
    super();
    this.mockData = {
      instances: [
        {
          instanceId: 'i-1234567890abcdef0',
          instanceType: 't3.micro',
          state: 'running',
          launchTime: '2025-10-01T10:00:00Z',
          availabilityZone: 'us-east-1a',
          publicIpAddress: '54.123.45.67',
          privateIpAddress: '172.31.1.100',
          tags: {
            Name: 'Web Server 1',
            Environment: 'Dev',
            Project: 'MyApp',
            Owner: 'TeamA'
          },
          metadata: {
            schedule: {
              StartTime: '09:00',
              StopTime: '18:00',
              Enabled: true,
              DaysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
            },
            lastModified: '2025-10-02T08:30:00Z',
            notes: 'Development web server'
          }
        },
        {
          instanceId: 'i-0987654321fedcba0',
          instanceType: 't3.small',
          state: 'stopped',
          launchTime: '2025-09-28T14:30:00Z',
          availabilityZone: 'us-east-1b',
          publicIpAddress: null,
          privateIpAddress: '172.31.2.200',
          tags: {
            Name: 'Database Server',
            Environment: 'Dev',
            Project: 'MyApp',
            Owner: 'TeamB'
          },
          metadata: {
            schedule: {
              StartTime: '08:00',
              StopTime: '20:00',
              Enabled: false
            },
            lastModified: '2025-10-01T16:45:00Z',
            notes: 'Development database server'
          }
        }
      ]
    };
  }

  async get(endpoint, params = {}) {
    // Mock 지연 시간
    await new Promise(resolve => setTimeout(resolve, 500));

    if (endpoint === '/instances') {
      let instances = [...this.mockData.instances];
      
      // 필터 적용
      if (params.state) {
        instances = instances.filter(i => i.state === params.state);
      }
      if (params.environment) {
        instances = instances.filter(i => i.tags?.Environment === params.environment);
      }
      if (params.project) {
        instances = instances.filter(i => i.tags?.Project === params.project);
      }
      if (params.owner) {
        instances = instances.filter(i => i.tags?.Owner === params.owner);
      }

      return {
        success: true,
        count: instances.length,
        instances: instances
      };
    }

    return { success: false, error: 'Mock endpoint not found' };
  }

  async post(endpoint, data = {}) {
    await new Promise(resolve => setTimeout(resolve, 800));

    if (endpoint.includes('/control')) {
      // Mock 인스턴스 제어
      return {
        success: true,
        instanceId: data.instanceId || endpoint.split('/')[2],
        action: data.action,
        timestamp: new Date().toISOString()
      };
    }

    if (endpoint.includes('/tags')) {
      // Mock 태그 관리
      return {
        success: true,
        instanceId: endpoint.split('/')[2],
        action: data.action,
        timestamp: new Date().toISOString(),
        currentTags: data.tags || {}
      };
    }

    return { success: false, error: 'Mock endpoint not found' };
  }
}

// 환경에 따른 서비스 선택
const isDevelopment = process.env.NODE_ENV === 'development' && !process.env.REACT_APP_API_BASE_URL;
export const apiService = isDevelopment ? new MockApiService() : new ApiService();

export default apiService;